
import {defineComponent, PropType} from "vue";
import {ProductCategoryRestService} from "@/services/rest/cpc-product-category-mgmt/ProductCategoryRestService";
import {
  CustomerCategoryImageRestDto,
  CustomerProductCategoryRestDto
} from "@/api/pharma-cpc-product-category-mgmt/models";
import {StatusEnum} from "@/api/enums/status-enum";
import {
  CustomerProductCategoryRestDtoModel
} from "@/models/api/pharma-cpc-product-category-mgmt/CustomerProductCategoryRestDtoModel";
import {
  CustomerCategoryImageRestDtoModel
} from "@/models/api/pharma-cpc-product-category-mgmt/CustomerCategoryImageRestDtoModel";

import InputText from "@/components/UI/InputText.vue";
import InputSelect from "@/components/UI/InputSelect.vue";
import CategoryImageEditor from "./CategoryImageEditor.vue";
import GenericFormWrapper from "@/components/base/GenericFormWrapper.vue";
import {FormMixin} from "@/components/base/FormMixin";
import {PageDetailModeEnum} from "@/models/enum/PageDetailModeEnum";
import {ImageRestDto} from "@/components/layouts/catalog/categories/ImageRestDto";
import BaseOffcanvasButtonClose from "@/components/UI/Bootstrap/Offcanvas/BaseOffcanvasButtonClose.vue";

export default defineComponent({
  name: "CustomerMainCategory",
  mixins: [FormMixin],
  components: {
    BaseOffcanvasButtonClose,
    InputText,
    InputSelect,
    CategoryImageEditor,
    GenericFormWrapper
  },
  emits: ["edit"],
  props: {
    code: { type: String, required: false },
    mode: { type: String, required: true },
    rootCategoryCode: { type: String, required: true },
    allLanguages: { type: Array as PropType<string[]>, required: true }
  },
  data() {
    return {
      docType: "CATEGORY_IMG" as const,
      category: CustomerProductCategoryRestDtoModel.createWithDefaults() as CustomerProductCategoryRestDto,
      customerCategoryImage: CustomerCategoryImageRestDtoModel.createWithDefaults() as CustomerCategoryImageRestDto,
      imageRestDto: {} as ImageRestDto,
      publishedStatus: StatusEnum.PUBLISHED,
      categoryCode: null as string | null,
      categoryMode: PageDetailModeEnum.NEW
    };
  },
  mounted() {
    this.reloadContent();
  },
  computed: {
    StatusEnum() {
      return StatusEnum;
    },
  },
  methods: {
    async doReload() {
      if (this.mode === PageDetailModeEnum.EDIT) {
        this.categoryMode = PageDetailModeEnum.EDIT;
        this.categoryCode = this.code!;
      }

      if (this.categoryMode === PageDetailModeEnum.EDIT) {
        this.category = await ProductCategoryRestService.getInstance()
          .getCustomerProductCategory(this.categoryCode!);
        const image = await ProductCategoryRestService.getInstance()
          .getProductCategoryCustomerImage(this.categoryCode!);
        this.customerCategoryImage = image ?? CustomerCategoryImageRestDtoModel.createWithDefaults();
      } else {
        this.category = CustomerProductCategoryRestDtoModel.createWithDefaults();
        this.customerCategoryImage = CustomerCategoryImageRestDtoModel.createWithDefaults();
      }
      this.imageRestDto = {
        image_document_key: this.customerCategoryImage.image_document_key,
        image: this.customerCategoryImage.image,
        svg: this.customerCategoryImage.svg,
      };

      this.publishedStatus = this.customerCategoryImage.is_hidden
        ? StatusEnum.NOT_PUBLISHED
        : StatusEnum.PUBLISHED;
    },
    async doSubmit() {
      if (this.categoryMode === PageDetailModeEnum.NEW) {
        this.category.dp_managed_parent_category_code = this.rootCategoryCode;
        this.categoryCode = await ProductCategoryRestService.getInstance()
          .addCustomerProductCategory(this.category);
      } else {
        await ProductCategoryRestService.getInstance()
          .updateCustomerProductCategory(this.category, this.categoryCode!);
      }

      // Refresh the category object after create/update
      this.category = await ProductCategoryRestService.getInstance()
        .getCustomerProductCategory(this.categoryCode!);

      this.categoryMode = PageDetailModeEnum.EDIT;

      this.customerCategoryImage.is_hidden = this.publishedStatus === StatusEnum.NOT_PUBLISHED;

      await ProductCategoryRestService.getInstance()
        .updateProductCategoryCustomerImage(this.customerCategoryImage, this.categoryCode!);

      // Refresh the image in case the backend generated something
      const refreshedImage = await ProductCategoryRestService.getInstance()
        .getProductCategoryCustomerImage(this.categoryCode!);

      this.customerCategoryImage = refreshedImage ?? CustomerCategoryImageRestDtoModel.createWithDefaults();

      this.$emit('edit');
    },
    onImageChanged() {
      this.customerCategoryImage = {
        ...this.customerCategoryImage,
        ...this.imageRestDto
      };
      this.onFormDataChanged();
    }
  }
});
